import { useState } from 'react';
import { motion } from 'motion/react';
import { X, MapPin, Clock, Phone, Star, Calendar, Users, Instagram, Facebook, Globe } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import { Input } from './ui/Input';

export function RestaurantDetailModal({ restaurant, onClose }) {
  const [reservationData, setReservationData] = useState({
    date: '',
    time: '',
    guests: '2',
    name: '',
    email: '',
    phone: '',
    specialRequest: '',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'owner'

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setReservationData({ ...reservationData, date, time: '' });

    // Simulate checking availability for the selected date
    const slots = [
      '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM',
      '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM',
      '9:00 PM', '9:30 PM'
    ];

    // Mock availability - in real app this would be an API call
    const availability = slots.map(time => ({
      time,
      available: Math.random() > 0.3 // 70% chance of being available
    }));

    setAvailableSlots(availability);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-screen px-4 py-8 flex items-start justify-center">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ duration: 0.4 }}
          className="bg-background max-w-5xl w-full shadow-2xl relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 z-10 w-10 h-10 border border-border hover:border-foreground bg-background flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Hero Image */}
          <div className="relative h-96 overflow-hidden">
            <ImageWithFallback
              src={restaurant.images?.[0] || ''}
              alt={restaurant.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
          </div>

          <div className="p-12">
            {/* Header */}
            <div className="mb-12">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="serif text-6xl mb-3">{restaurant.name}</h1>
                  <p className="text-sm text-muted-foreground uppercase tracking-widest mb-4">
                    {restaurant.cuisine} · {restaurant.priceRange}
                  </p>
                </div>
                <div className="flex items-center gap-2 bg-stone-100 px-4 py-2">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="text-sm">{restaurant.rating}</span>
                  <span className="text-xs text-muted-foreground">({restaurant.reviews})</span>
                </div>
              </div>

              {/* Tab Navigation */}
              {restaurant.owner && (
                <div className="flex items-center gap-8 border-b border-border mb-8">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`
                      pb-4 text-sm uppercase tracking-widest transition-colors relative
                      ${activeTab === 'overview' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}
                    `}
                  >
                    Overview
                    {activeTab === 'overview' && (
                      <motion.div
                        layoutId="restaurantTab"
                        className="absolute bottom-0 left-0 right-0 h-px bg-foreground"
                        transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
                      />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('owner')}
                    className={`
                      pb-4 text-sm uppercase tracking-widest transition-colors relative
                      ${activeTab === 'owner' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}
                    `}
                  >
                    Meet the Owner
                    {activeTab === 'owner' && (
                      <motion.div
                        layoutId="restaurantTab"
                        className="absolute bottom-0 left-0 right-0 h-px bg-foreground"
                        transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
                      />
                    )}
                  </button>
                </div>
              )}

              {activeTab === 'overview' && (
                <>
                  <p className="text-muted-foreground leading-relaxed max-w-3xl mb-8">
                    {restaurant.description}
                  </p>

                  {/* Info Grid */}
                  <div className="grid grid-cols-3 gap-8 pb-8 border-b border-border">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Address</p>
                        <p className="text-sm">{restaurant.address}</p>
                      </div>
                    </div>
                    {restaurant.hours && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Hours</p>
                          <p className="text-sm">{restaurant.hours}</p>
                        </div>
                      </div>
                    )}
                    {restaurant.phone && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Phone</p>
                          <p className="text-sm">{restaurant.phone}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Specialties */}
            {restaurant.specialties && restaurant.specialties.length > 0 && (
              <div className="mb-12">
                <h3 className="text-sm uppercase tracking-widest mb-4">Signature Dishes</h3>
                <div className="flex flex-wrap gap-2">
                  {restaurant.specialties.map((specialty) => (
                    <span
                      key={specialty}
                      className="px-4 py-2 border border-border text-xs"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Meet the Owner Content */}
            {activeTab === 'owner' && restaurant.owner && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-12"
              >
                {/* Owner Profile */}
                <div className="grid grid-cols-12 gap-12 mb-16">
                  <div className="col-span-4">
                    <div className="aspect-[3/4] overflow-hidden mb-6">
                      <ImageWithFallback
                        src={restaurant.owner.photo}
                        alt={restaurant.owner.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="col-span-8 flex flex-col justify-center">
                    <div className="w-1 h-16 bg-border mb-6"></div>
                    <h2 className="serif text-5xl mb-4">{restaurant.owner.name}</h2>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
                      Owner & Founder
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {restaurant.owner.bio}
                    </p>

                    {/* Social Links */}
                    {restaurant.owner.social && (
                      <div className="flex items-center gap-4 pt-6 border-t border-border">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">
                          Connect
                        </p>
                        <div className="flex items-center gap-3">
                          {restaurant.owner.social.instagram && (
                            <a
                              href={restaurant.owner.social.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-9 h-9 border border-border hover:border-foreground flex items-center justify-center transition-colors"
                            >
                              <Instagram className="w-4 h-4" />
                            </a>
                          )}
                          {restaurant.owner.social.facebook && (
                            <a
                              href={restaurant.owner.social.facebook}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-9 h-9 border border-border hover:border-foreground flex items-center justify-center transition-colors"
                            >
                              <Facebook className="w-4 h-4" />
                            </a>
                          )}
                          {restaurant.owner.social.website && (
                            <a
                              href={restaurant.owner.social.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-9 h-9 border border-border hover:border-foreground flex items-center justify-center transition-colors"
                            >
                              <Globe className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Owner's Story */}
                {restaurant.owner.story && (
                  <div className="border-t border-border pt-12 mb-12">
                    <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
                      The Story
                    </h3>
                    <p className="serif text-2xl leading-relaxed text-muted-foreground mb-8">
                      {restaurant.owner.story}
                    </p>
                    {restaurant.owner.favoritedish && (
                      <div className="bg-stone-50 p-8 border border-border">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                          Favorite Dish
                        </p>
                        <p className="serif text-3xl">{restaurant.owner.favoritedish}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Interview Q&A */}
                {restaurant.owner.interview && restaurant.owner.interview.length > 0 && (
                  <div className="border-t border-border pt-12">
                    <h3 className="serif text-4xl mb-8">In Conversation</h3>
                    <div className="space-y-10">
                      {restaurant.owner.interview.map((qa, index) => (
                        <div key={index} className="border-l-2 border-border pl-8">
                          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
                            Q{index + 1}
                          </p>
                          <h4 className="serif text-2xl mb-4">{qa.question}</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            {qa.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Gallery */}
            {activeTab === 'overview' && restaurant.images && restaurant.images.length > 1 && (
              <div className="grid grid-cols-3 gap-4 mb-12">
                {restaurant.images.slice(1).map((image, index) => (
                  <div key={index} className="aspect-[4/3] overflow-hidden">
                    <ImageWithFallback
                      src={image}
                      alt={`${restaurant.name} ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Reservation Form */}
            {activeTab === 'overview' && (
              <div className="border-t border-border pt-12">
                <h2 className="serif text-4xl mb-2">Make a Reservation</h2>
                <p className="text-sm text-muted-foreground mb-8">
                  Reserve your table and we'll confirm your booking shortly
                </p>

                {!isSubmitted ? (
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-3 gap-6 mb-6">
                      {/* Date */}
                      <div className="col-span-1">
                        <label className="text-xs uppercase tracking-widest mb-3 block">
                          Date
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="date"
                            required
                            value={reservationData.date}
                            onChange={(e) => handleDateChange(e.target.value)}
                            className="pl-10 h-12 border-border"
                          />
                        </div>
                      </div>

                      {/* Guests */}
                      <div className="col-span-1">
                        <label className="text-xs uppercase tracking-widest mb-3 block">
                          Guests
                        </label>
                        <div className="relative">
                          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <select
                            required
                            value={reservationData.guests}
                            onChange={(e) => setReservationData({ ...reservationData, guests: e.target.value })}
                            className="w-full h-12 pl-10 pr-4 border border-border bg-background text-sm appearance-none cursor-pointer"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                              <option key={num} value={num}>
                                {num} {num === 1 ? 'Guest' : 'Guests'}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Available Time Slots */}
                    {availableSlots.length > 0 && (
                      <div className="mb-8">
                        <label className="text-xs uppercase tracking-widest mb-4 block">
                          Available Times
                        </label>
                        <div className="grid grid-cols-5 gap-3">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot.time}
                              type="button"
                              disabled={!slot.available}
                              onClick={() => setReservationData({ ...reservationData, time: slot.time })}
                              className={`
                                h-12 border text-sm transition-all
                                ${!slot.available
                                  ? 'border-border bg-stone-50 text-muted-foreground cursor-not-allowed line-through'
                                  : reservationData.time === slot.time
                                    ? 'border-foreground bg-foreground text-background'
                                    : 'border-border hover:border-foreground'
                                }
                              `}
                            >
                              {slot.time}
                            </button>
                          ))}
                        </div>
                        {selectedDate && (
                          <p className="text-xs text-muted-foreground mt-3">
                            {availableSlots.filter(s => s.available).length} of {availableSlots.length} slots available
                          </p>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-6 mb-6">
                      {/* Name */}
                      <div>
                        <label className="text-xs uppercase tracking-widest mb-3 block">
                          Full Name
                        </label>
                        <Input
                          type="text"
                          required
                          placeholder="John Doe"
                          value={reservationData.name}
                          onChange={(e) => setReservationData({ ...reservationData, name: e.target.value })}
                          className="h-12 border-border"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="text-xs uppercase tracking-widest mb-3 block">
                          Email Address
                        </label>
                        <Input
                          type="email"
                          required
                          placeholder="john@example.com"
                          value={reservationData.email}
                          onChange={(e) => setReservationData({ ...reservationData, email: e.target.value })}
                          className="h-12 border-border"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="text-xs uppercase tracking-widest mb-3 block">
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        required
                        placeholder="+1 (555) 000-0000"
                        value={reservationData.phone}
                        onChange={(e) => setReservationData({ ...reservationData, phone: e.target.value })}
                        className="h-12 border-border"
                      />
                    </div>

                    <div className="mb-8">
                      <label className="text-xs uppercase tracking-widest mb-3 block">
                        Special Requests (Optional)
                      </label>
                      <textarea
                        placeholder="Dietary restrictions, special occasions, seating preferences..."
                        value={reservationData.specialRequest}
                        onChange={(e) => setReservationData({ ...reservationData, specialRequest: e.target.value })}
                        className="w-full min-h-24 px-4 py-3 border border-border bg-background text-sm resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full h-14 bg-foreground text-background hover:bg-foreground/90 transition-colors text-sm uppercase tracking-widest"
                    >
                      Confirm Reservation
                    </button>
                  </form>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 rounded-full border-2 border-foreground flex items-center justify-center mx-auto mb-6">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        ✓
                      </motion.div>
                    </div>
                    <h3 className="serif text-3xl mb-3">Reservation Confirmed</h3>
                    <p className="text-sm text-muted-foreground">
                      We've sent a confirmation email to {reservationData.email}
                    </p>
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

